import { useMutation } from '@apollo/client';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useState } from 'react';

import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { BATCH_RETRY_SCORE_JOB_APPLICATIONS } from '@gql/jobs';

const BatchScoreRetryPanel: React.FC = () => {
    const theme = useTheme();
    const { showSnackbar } = useSnackbar();
    const [confirmOpen, setConfirmOpen] = useState(false);

    const [batchRetry, { loading }] = useMutation(BATCH_RETRY_SCORE_JOB_APPLICATIONS, {
        onCompleted: (data) => {
            const result = data?.batchRetryScoreJobApplications;
            if (result) {
                const parts: string[] = [];
                if (result.trimojiTriggeredCount > 0) {
                    parts.push(`${result.trimojiTriggeredCount} CV envoyé(s) à Trimoji (2-5 min)`);
                }
                if (result.directRetryCount > 0) {
                    parts.push(`${result.directRetryCount} scoring(s) relancé(s) directement`);
                }
                const message =
                    parts.length > 0
                        ? parts.join(', ') + ` sur ${result.totalFound} candidature(s) en erreur.`
                        : `Aucune candidature traitée (${result.totalFound} trouvée(s)).`;
                showSnackbar(message, 'success');
            }
        },
        onError: () => {
            showSnackbar('Erreur lors du relancement en masse du scoring.', 'error');
        },
    });

    const handleConfirm = () => {
        setConfirmOpen(false);
        batchRetry({ variables: { limit: 50 } });
    };

    return (
        <Box>
            <Alert severity="warning" sx={{ mb: 2, color: theme.palette.text.primary }}>
                Certaines candidatures ont des erreurs de scoring. Le relancement est{' '}
                <strong>immédiat</strong> pour les CVs déjà analysés, et prend{' '}
                <strong>2 à 5 minutes</strong> pour les CVs non encore envoyés à Trimoji.
            </Alert>

            <Button
                variant="outlined"
                disabled={loading}
                onClick={() => setConfirmOpen(true)}
                startIcon={loading ? <CircularProgress size={16} /> : undefined}
            >
                {loading ? 'Relancement en cours...' : 'Relancer le scoring en masse (50 max)'}
            </Button>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Typography variant="h6">Confirmer le relancement en masse</Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                        Cette action va relancer le scoring pour jusqu'à 50 candidatures en erreur.
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        — Pour les candidatures <strong>sans resumeId</strong> : le CV sera renvoyé à Trimoji
                        (résultat dans 2-5 min).
                        <br />— Pour les candidatures <strong>avec resumeId</strong> : le scoring sera relancé
                        immédiatement.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} variant="text" color="secondary">
                        Annuler
                    </Button>
                    <Button onClick={handleConfirm} variant="contained">
                        Confirmer
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BatchScoreRetryPanel;
